import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements } from '@stripe/react-stripe-js';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import {
  useSubscription,
  useCreditBalance,
  useCreditTransactions,
  usePlans,
  useChangePlan,
  useOnboardingStatus,
  useCurrentPeriod,
  useRetryAdvanceCharge,
  useCancelSubscription,
  useReactivateSubscription,
} from '../../hooks/useBilling';
import { CurrentPlanCard } from './CurrentPlanCard';
import { CreditUsageCard } from './CreditUsageCard';
import { UsageBreakdown } from './UsageBreakdown';
import { PlanSelector } from './PlanSelector';
import { CreditPackCard } from './CreditPackCard';
import { CurrentPeriodCard } from './CurrentPeriodCard';
import { PaymentActionRequired } from './PaymentActionRequired';
import { CancellationSection } from './CancellationSection';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';
import { stripePublishableKey } from '../../boot/deployment-keys';

export function BillingSection() {
  const stripeKey = stripePublishableKey();
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const { data: subscription, isLoading: subLoading } = useSubscription();
  const { data: balance, isLoading: balLoading } = useCreditBalance();
  const { data: transactions } = useCreditTransactions({ limit: 500 });
  const { data: plans } = usePlans();
  const changePlan = useChangePlan();
  const { data: onboardingStatus } = useOnboardingStatus();
  const { data: periodPosition } = useCurrentPeriod();
  const retryAdvance = useRetryAdvanceCharge();
  const cancelSubscription = useCancelSubscription();
  const reactivateSubscription = useReactivateSubscription();

  const needsRetryElements = periodPosition?.status === 'awaiting_payment';
  const stripePromise = useMemo(() => {
    if (!needsRetryElements || !stripeKey) {
      return null;
    }
    return loadStripe(stripeKey);
  }, [needsRetryElements, stripeKey]);

  if (!isAuthenticated) {
    return (
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Billing</h2>
          <p className="text-sm text-muted-foreground">Sign in to view billing information.</p>
        </div>
      </section>
    );
  }

  if (subLoading || balLoading) {
    return (
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Billing</h2>
        </div>
        <LoadingState label="Loading billing data..." />
      </section>
    );
  }

  const isPlatformPlan =
    subscription?.plan_name?.startsWith('platform_') ||
    subscription?.plan_name === 'platform_30' ||
    subscription?.plan_name === 'platform_trial_3day';
  const isOnTrial = subscription?.plan_name === 'platform_trial_3day';
  const isComplimentary = onboardingStatus?.never_bill ?? false;

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Billing</h2>
        <p className="text-sm text-muted-foreground">Plan, credits, and usage</p>
      </div>

      {isPlatformPlan && (
        <div className="rounded-lg bg-card p-4 space-y-2" data-testid="billing-platform-plan">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Platform Plan</h3>
              <p className="text-lg font-semibold" data-testid="billing-plan-status">
                {isOnTrial ? 'Trial' : 'Platform'}
              </p>
            </div>
            <div className="text-right">
              {isComplimentary ? (
                <span
                  className="inline-block rounded bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400"
                  data-testid="billing-plan-badge"
                >
                  Complimentary
                </span>
              ) : isOnTrial ? (
                <span
                  className="inline-block rounded bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-400"
                  data-testid="billing-plan-badge"
                >
                  3-day trial
                </span>
              ) : (
                <p className="text-lg font-semibold" data-testid="billing-plan-badge">
                  $30/mo
                </p>
              )}
            </div>
          </div>
          {isOnTrial && !isComplimentary && (
            <p className="text-xs text-muted-foreground" data-testid="billing-plan-price">
              $30/mo after trial
            </p>
          )}
          {isOnTrial && onboardingStatus?.trial_ends_at && (
            <p className="text-xs text-muted-foreground" data-testid="billing-trial-ends">
              Trial ends{' '}
              {new Date(onboardingStatus.trial_ends_at).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          )}
        </div>
      )}

      {isPlatformPlan &&
        periodPosition &&
        (periodPosition.status === 'awaiting_payment' ||
          periodPosition.status === 'settlement_failed') &&
        (periodPosition.status === 'awaiting_payment' && stripePromise ? (
          <Elements
            stripe={stripePromise}
            options={{
              mode: 'setup',
              currency: 'usd',
              appearance: {
                theme: 'stripe',
                variables: { colorPrimary: '#000000' },
              },
            }}
          >
            <PaymentActionRequired
              position={periodPosition}
              onRetryAdvanceCharge={async () => {
                await retryAdvance.mutateAsync();
              }}
              isRetrying={retryAdvance.isPending}
              showPaymentElement
            />
          </Elements>
        ) : (
          <PaymentActionRequired
            position={periodPosition}
            onRetryAdvanceCharge={
              periodPosition.status === 'awaiting_payment'
                ? async () => {
                    await retryAdvance.mutateAsync();
                  }
                : undefined
            }
            isRetrying={retryAdvance.isPending}
          />
        ))}

      {isPlatformPlan && periodPosition && (
        <CurrentPeriodCard position={periodPosition} />
      )}

      {subscription && !isPlatformPlan && <CurrentPlanCard subscription={subscription} />}

      {balance && subscription && !isPlatformPlan && (
        <CreditUsageCard
          balance={balance}
          includedCredits={subscription.included_credits}
          periodEnd={subscription.current_period_end}
        />
      )}

      {transactions && !isPlatformPlan && <UsageBreakdown transactions={transactions} />}

      {plans && subscription && !isPlatformPlan && (
        <PlanSelector
          plans={plans}
          currentPlanId={subscription.plan_id}
          onChangePlan={(planId) =>
            changePlan.mutate({
              subscription_id: subscription.subscription_id,
              new_plan_id: planId,
            })
          }
          isChanging={changePlan.isPending}
        />
      )}

      {/* De-emphasize credit pack for V1 self-serve platform plan users */}
      {!isPlatformPlan && (
        <CreditPackCard
          onPurchase={() => {
            console.log('Credit pack purchase initiated');
          }}
          isPurchasing={false}
        />
      )}

      {/* Cancellation section for paid platform plan users */}
      {isPlatformPlan && !isComplimentary && onboardingStatus?.phase === 'complete' && (
        <CancellationSection
          cancellationState={subscription?.cancellation_state}
          onCancel={async (reason) => {
            await cancelSubscription.mutateAsync(reason ? { reason } : undefined);
          }}
          onReactivate={async () => {
            await reactivateSubscription.mutateAsync();
          }}
          isCancelling={cancelSubscription.isPending}
          isReactivating={reactivateSubscription.isPending}
          isTrial={isOnTrial}
          previewEffectiveAt={
            isOnTrial
              ? (onboardingStatus?.trial_ends_at ?? subscription?.current_period_end)
              : (periodPosition?.period_end ?? subscription?.current_period_end)
          }
        />
      )}
    </section>
  );
}
